// LeadCompliancePanel — Phase 5.
//
// Compliance checklist for this lead — one row per item with a
// pass/warn/fail dot. Signals derive from the lead payload itself
// (consent flags, opt-out flags, phone e164 format, trustedForm
// certificate, SOA record) — no new endpoints.

import React from 'react';

function StatusDot({ status }) {
  const color = status === 'pass' ? '#059669' : status === 'warn' ? '#d97706' : status === 'fail' ? '#dc2626' : '#94a3b8';
  return <span style={{
    display: 'inline-block', width: 8, height: 8, borderRadius: 999,
    background: color, boxShadow: `0 0 0 3px ${color}22`,
  }}/>;
}

function checkTcpa(lead) {
  if (lead?.doNotCall) return { status: 'fail', note: 'On do-not-call list' };
  if (lead?.trustedFormCert || lead?.tcpaConsent) return { status: 'pass', note: 'Consent captured' };
  return { status: 'warn', note: 'No consent record — check source' };
}

function checkA2P(lead) {
  if (lead?.optOutSms) return { status: 'fail', note: 'Opted out of SMS' };
  if (!lead?.phone)    return { status: 'warn', note: 'No phone on file' };
  return { status: 'pass', note: 'Phone on file, opt-in active' };
}

function checkRecording(lead) {
  // Recording consent — presume required unless explicitly captured.
  if (lead?.recordingConsentAt) return { status: 'pass', note: 'Consent on record' };
  return { status: 'warn', note: 'Capture at start of next call' };
}

function checkSOA(lead) {
  const v = String(lead?.vertical || '').toLowerCase();
  if (v !== 'medicare' && v !== 'senior') return { status: 'na', note: 'Not applicable' };
  if (lead?.soaSignedAt) return { status: 'pass', note: 'Signed SOA on file' };
  return { status: 'warn', note: 'SOA needed before quote' };
}

function checkEmail(lead) {
  if (!lead?.email)      return { status: 'warn', note: 'No email captured' };
  if (lead?.optOutEmail) return { status: 'fail', note: 'Opted out' };
  return { status: 'pass', note: 'Deliverable' };
}

function checkPhoneFormat(lead) {
  if (!lead?.phone) return { status: 'warn', note: 'No phone' };
  const cleaned = String(lead.phone).replace(/\D/g, '');
  if (cleaned.length === 10 || (cleaned.length === 11 && cleaned.startsWith('1'))) {
    return { status: 'pass', note: 'Valid US format' };
  }
  return { status: 'warn', note: 'Not US 10-digit format' };
}

export default function LeadCompliancePanel({ lead }) {
  const items = [
    { key: 'tcpa',      label: 'TCPA consent',      ...checkTcpa(lead) },
    { key: 'a2p',       label: 'A2P (SMS eligible)',...checkA2P(lead) },
    { key: 'recording', label: 'Recording consent', ...checkRecording(lead) },
    { key: 'soa',       label: 'Medicare SOA',      ...checkSOA(lead) },
    { key: 'email',     label: 'Email deliverable', ...checkEmail(lead) },
    { key: 'phone',     label: 'Phone format',      ...checkPhoneFormat(lead) },
  ].filter(i => i.status !== 'na');

  const passing = items.filter(i => i.status === 'pass').length;
  const failing = items.filter(i => i.status === 'fail').length;

  return (
    <div style={{
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: 10, padding: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#334155', fontWeight: 800 }}>
          Compliance
        </div>
        <div style={{ fontSize: 11, color: '#475569' }}>
          {passing}/{items.length} passing{failing > 0 && ` · ${failing} blocking`}
        </div>
      </div>
      <div style={{ display: 'grid', gap: 4 }}>
        {items.map(i => (
          <div key={i.key} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: '#ffffff', border: '1px solid #e2e8f0',
            borderRadius: 6, padding: '6px 10px',
          }}>
            <StatusDot status={i.status}/>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, color: '#0f172a', fontWeight: 700 }}>{i.label}</div>
              <div style={{ fontSize: 10, color: '#64748b', marginTop: 1 }}>{i.note}</div>
            </div>
            <div style={{
              fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase',
              color: i.status === 'pass' ? '#059669' : i.status === 'warn' ? '#d97706' : '#dc2626',
              fontWeight: 800,
            }}>{i.status}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
